import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { useTourismActions } from '../../context/TourismContext';
import { getImageUrl } from '../../services/api';

function SaleOne() {
    const [sectionData, setSectionData] = useState(null);
    const [accommodations, setAccommodations] = useState([]);
    const [loading, setLoading] = useState(true);
    const { fetchAirbnbPropertySection, fetchFeaturedAccommodations } = useTourismActions();

    useEffect(() => {
        const loadData = async () => {
            try {
                console.log('Loading Property Section data...');
                
                // Load section data
                const sectionResponse = await fetchAirbnbPropertySection();
                console.log('Section API Response:', sectionResponse);
                const section = sectionResponse?.results?.[0] || sectionResponse?.[0] || null;
                setSectionData(section);

                // Load featured accommodations (yachts/properties)
                const accommodationsResponse = await fetchFeaturedAccommodations();
                console.log('Accommodations API Response:', accommodationsResponse);
                const properties = accommodationsResponse?.results || accommodationsResponse || [];
                setAccommodations(properties.slice(0, 6)); // Limit to 6 items
                
            } catch (error) {
                console.error('Error loading section data:', error);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [fetchAirbnbPropertySection, fetchFeaturedAccommodations]);

    // Use dynamic data or fallback to static content
    const content = sectionData || {
        subtitle: "For Sale & Rent",
        title: "Choose Your Perfect Yacht",
        description: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
        button_text: "View All Properties",
        button_link: "/accommodation",
        background_image: null
    };

    const backgroundStyle = {
        backgroundImage: content.background_image 
            ? `url('${getImageUrl(content.background_image)}')`
            : "url('/assets/img/bg/shape_bg_1.png')"
    };

    console.log('Final content to render:', content);
    console.log('Accommodations to render:', accommodations);

    if (loading) {
        return (
            <section className="position-relative bg-smoke overflow-hidden space" id="service-sec">
                <div className="container">
                    <div className="text-center">Loading...</div>
                </div>
            </section>
        );
    }

    return (
        <section
            className="position-relative bg-smoke overflow-hidden space"
            id="service-sec"
            style={backgroundStyle}
        >
            <div className="container">
                <div className="row">
                    <div className="col-lg-6 offset-lg-3">
                        <div className="title-area text-center">
                            <span className="sub-title">{content.subtitle || "For Sale & Rent"}</span>
                            <h2 className="sec-title">{content.title || "Choose Your Perfect Yacht"}</h2>
                            {content.description && (
                                <p className="sec-text">
                                    {content.description}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
                <div className="slider-area tour-slider">
                    <Swiper
                        modules={[Autoplay]}
                        spaceBetween={24}
                        slidesPerView={1}
                        loop={accommodations.length > 1}
                        breakpoints={{
                            576: { slidesPerView: 1 },
                            768: { slidesPerView: 2 },
                            992: { slidesPerView: 2 },
                            1200: { slidesPerView: 3 },
                        }}
                        autoplay={accommodations.length > 1 ? { delay: 3000, disableOnInteraction: false } : false}
                        className="th-slider has-shadow slider-drag-wrap"
                    >
                        {accommodations.length > 0 ? (
                            accommodations.map((accommodation, index) => {
                                const ratingPercentage = accommodation.rating ? (accommodation.rating / 5) * 100 : 100;
                                const imageUrl = getImageUrl(accommodation.main_image) || '/assets/img/tour/tour-1_1.jpg';
                                
                                return (
                                    <SwiperSlide key={accommodation.id || index}>
                                        <div className="tour-box style2 th-ani gsap-cursor">
                                            <div className="tour-box_img global-img">
                                                <img src={imageUrl} alt={accommodation.name || 'Property'} />
                                            </div>
                                            <div className="tour-content">
                                                <h3 className="box-title">
                                                    <Link to={`/accommodations/${accommodation.slug || accommodation.id}`}>
                                                        {accommodation.name || 'Luxury Property'}
                                                    </Link>
                                                </h3>
                                                <div className="tour-rating">
                                                    <div
                                                        className="star-rating"
                                                        role="img"
                                                        aria-label={`Rated ${accommodation.rating || 5} out of 5`}
                                                    >
                                                        <span style={{ width: `${ratingPercentage}%` }}>
                                                            Rated
                                                            <strong className="rating">{accommodation.rating || '5.00'}</strong> out of 5
                                                        </span>
                                                    </div>
                                                    <Link
                                                        to={`/accommodations/${accommodation.slug || accommodation.id}`}
                                                        className="woocommerce-review-link"
                                                    >
                                                        (<span className="count">{accommodation.rating || '5.0'}</span>
                                                        Rating)
                                                    </Link>
                                                </div>
                                                <div className="tour-list">
                                                    <ul>
                                                        <li>
                                                            Bedrooms<span>{accommodation.bedrooms || '1'}</span>
                                                        </li>
                                                        <li>
                                                            Capacity<span>{accommodation.max_guests || '2'}</span>
                                                        </li>
                                                        <li>
                                                            Type<span>{accommodation.accommodation_type?.name || 'Villa'}</span>
                                                        </li>
                                                    </ul>
                                                </div>
                                                <div className="tour-action">
                                                    <h4 className="tour-box_price">
                                                        <span className="currency">${accommodation.price_per_night || '980.00'}</span>/per night
                                                    </h4>
                                                    <Link to={`/accommodations/${accommodation.slug || accommodation.id}`} className="th-btn style4 th-icon">
                                                        Book Now
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    </SwiperSlide>
                                );
                            })
                        ) : (
                            // Fallback static slides when no accommodations data
                            [
                                {
                                    id: 1,
                                    name: "Aquavita Boat",
                                    image: "/assets/img/tour/tour-1_1.jpg",
                                    rating: 4.8,
                                    price: "980.00",
                                    length: "50 m",
                                    capacity: "08",
                                    type: "Catamaran"
                                },
                                {
                                    id: 2,
                                    name: "Golden Odyssey",
                                    image: "/assets/img/tour/tour-1_2.jpg",
                                    rating: 4.8,
                                    price: "1200.00",
                                    length: "65 m",
                                    capacity: "12",
                                    type: "Motor Yacht"
                                },
                                {
                                    id: 3,
                                    name: "Luxury Motor Yacht",
                                    image: "/assets/img/tour/tour-1_3.jpg",
                                    rating: 4.8,
                                    price: "1500.00",
                                    length: "80 m",
                                    capacity: "16",
                                    type: "Super Yacht"
                                }
                            ].map((yacht) => (
                                <SwiperSlide key={yacht.id}>
                                    <div className="tour-box style2 th-ani gsap-cursor">
                                        <div className="tour-box_img global-img">
                                            <img src={yacht.image} alt={yacht.name} />
                                        </div>
                                        <div className="tour-content">
                                            <h3 className="box-title">
                                                <Link to="/accommodation">{yacht.name}</Link>
                                            </h3>
                                            <div className="tour-rating">
                                                <div
                                                    className="star-rating"
                                                    role="img"
                                                    aria-label={`Rated ${yacht.rating} out of 5`}
                                                >
                                                    <span style={{ width: "96%" }}>
                                                        Rated
                                                        <strong className="rating">{yacht.rating}</strong> out of 5
                                                    </span>
                                                </div>
                                                <Link
                                                    to="/accommodation"
                                                    className="woocommerce-review-link"
                                                >
                                                    (<span className="count">{yacht.rating}</span>
                                                    Rating)
                                                </Link>
                                            </div>
                                            <div className="tour-list">
                                                <ul>
                                                    <li>
                                                        Length<span>{yacht.length}</span>
                                                    </li>
                                                    <li>
                                                        Capacity<span>{yacht.capacity}</span>
                                                    </li>
                                                    <li>
                                                        Type<span>{yacht.type}</span>
                                                    </li>
                                                </ul>
                                            </div>
                                            <div className="tour-action">
                                                <h4 className="tour-box_price">
                                                    <span className="currency">${yacht.price}</span>/per day
                                                </h4>
                                                <Link to="/accommodation" className="th-btn style4 th-icon">
                                                    Book Now
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </SwiperSlide>
                            ))
                        )}
                    </Swiper>
                </div>
                {content.button_text && content.button_link && (
                    <div className="row">
                        <div className="col-12 text-center mt-4">
                            <Link to={content.button_link} className="th-btn th-icon">
                                {content.button_text}
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

export default SaleOne;
